import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import LoadingView from '../../shared/components/LoadingView';
import FormDefinitionPreview from '../components/workflows/FormDefinitionPreview';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import useForms from '../hooks/useForms';
import useFormAiEditor from '../hooks/useFormAiEditor';

function EmptyPreview() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 bg-light border rounded">
      <i className="bi bi-stars fs-1 text-muted mb-3" />
      <h5 className="fw-semibold mb-2">Start with AI</h5>
      <p className="text-muted mb-0">Describe the form to the AI to create it</p>
    </div>
  );
}

export default function FormEditPage() {
  const SIDEBAR_WIDTH = 392;
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchFormById, uploadFormDefinition } = useForms();
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [chatText, setChatText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useDocumentTitle('Edit form – Maintella');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPageError('');
    fetchFormById(id)
      .then((data) => {
        if (!active) return;
        setInitialForm({
          slug: data.slug,
          title: data.title,
          description: data.description || '',
          json_schema: data.schema || {},
          ui_schema: data.uiSchema || {},
          defaults: data.defaults || {},
          meta: data.meta || {},
        });
      })
      .catch((err) => {
        if (!active) return;
        setPageError(err.message || 'Failed to load form');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchFormById, id]);

  const initialFormOrEmpty = useMemo(() => initialForm || {}, [initialForm]);

  const editor = useFormAiEditor({
    formId: id,
    initialForm: initialFormOrEmpty,
  });

  const hasPreviewableSchema =
    !!editor.draftForm?.json_schema &&
    Object.keys(editor.draftForm.json_schema).length > 0 &&
    editor.draftForm.json_schema.type === 'object' &&
    !!editor.draftForm.json_schema.properties &&
    Object.keys(editor.draftForm.json_schema.properties).length > 0;

  const onSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await uploadFormDefinition(editor.draftForm);
      editor.clearPersistedState();
      navigate('/admin/organization/forms');
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitMessage = () => {
    const value = chatText.trim();
    if (!value || editor.loading) return;
    setChatText('');
    editor.sendMessage(value);
  };

  if (loading) {
    return (
      <div className="container py-4">
        <LoadingView
          symbolClass="bi-file-earmark-text"
          title="Loading form"
          subtitle="Fetching form definition for editing."
          className="py-5"
        />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          {pageError}
        </div>
      </div>
    );
  }

  return (
    <>
      <ObjectTopBar title={`Edit Form: ${editor.draftForm.title || editor.draftForm.slug}`} />
      <div style={{ display: 'flex', width: '100%', minHeight: 'calc(100vh - 64px)' }}>
        <div className="flex-grow-1 p-4">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Form Preview</h5>
              {hasPreviewableSchema ? (
                <FormDefinitionPreview
                  schema={editor.draftForm.json_schema}
                  uiSchema={editor.draftForm.ui_schema}
                  defaults={editor.draftForm.defaults}
                />
              ) : (
                <EmptyPreview />
              )}
            </div>
          </div>
        </div>

        <div
          className="bg-white border-start d-flex flex-column"
          style={{ width: SIDEBAR_WIDTH, minHeight: 0, overflow: 'hidden' }}
        >
          <div className="px-3 pt-3 pb-2 flex-shrink-0 border-bottom bg-white">
            <div className="small text-muted font-monospace text-break mb-1">
              Form slug: {editor.draftForm.slug}
            </div>
            <h5 className="fw-semibold mb-0">AI Editor</h5>
          </div>

          <div className="flex-grow-1 overflow-auto px-3 py-3" style={{ minHeight: 0 }}>
            {(saveError || editor.error) && (
              <div className="alert alert-danger mb-3" role="alert">
                {saveError || editor.error}
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label form-label-caps mb-0">Conversation</label>
              {editor.lastMeta?.model && <small className="text-muted">{editor.lastMeta.model}</small>}
            </div>

            <div className="overflow-auto border rounded p-2 bg-light mb-3" style={{ minHeight: 260 }}>
              {editor.messages.length === 0 ? (
                <p className="text-muted small mb-0">
                  Ask the AI to add fields, validations, help text, or improve structure.
                </p>
              ) : (
                editor.messages.map((m, idx) => (
                  <div
                    key={`${m.role}-${idx}`}
                    className={`mb-2 ${m.role === 'user' ? 'text-end' : ''}`}
                  >
                    <span
                      className={`d-inline-block px-2 py-1 rounded small ${
                        m.role === 'user' ? 'bg-primary text-white' : 'bg-white border'
                      }`}
                    >
                      {m.content}
                    </span>
                  </div>
                ))
              )}
            </div>

            {editor.validationErrors?.length > 0 && (
              <div className="alert alert-warning small py-2" role="alert">
                {editor.validationErrors.join(' | ')}
              </div>
            )}

            <div className="mb-2">
              <textarea
                className="form-control mb-2"
                rows={4}
                placeholder="Ask AI to modify the form... (Ctrl+Enter to send)"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !editor.loading) {
                    e.preventDefault();
                    submitMessage();
                  }
                }}
                style={{ resize: 'vertical', minHeight: '5rem' }}
              />
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={editor.loading || !chatText.trim()}
                  onClick={submitMessage}
                >
                  {editor.loading ? '...' : 'Send'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-top shadow-sm p-3 d-grid gap-2">
            <button
              type="button"
              className="btn btn-success w-100"
              onClick={onSave}
              disabled={saving || !editor.isDirty}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={() => navigate('/admin/organization/forms')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
